import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { useTracking } from '@/hooks/useTracking'
import { useAnalytics } from '@/contexts/AnalyticsContext'
import { getPendingUpload, clearPendingUpload } from '@/lib/pendingUpload'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useDispatch } from 'react-redux'
import { setMediaUri, setVideoFile, setImageFile } from '@/store/mediaSlice'
import { routes } from '@/routes/ProtectedRoutes'
import { FILE_TYPE } from '@/constants/fileTypes'
import { ProcessingProgress } from '@/components/ProcessingProgress'

const AnalyzerMap = {
  image: routes.image.path,
  video: routes.video.path,
}

export function OAuthCallback() {
  const navigate = useNavigate()
  const { authService } = useAuth()
  const { trackAuth, trackAnalysis } = useTracking()
  const { identifyUser, setUserProperties } = useAnalytics()
  const { uploadPendingFile } = useFileUpload()
  const dispatch = useDispatch()
  const [processingMessage, setProcessingMessage] = useState(
    'Completing sign in...',
  )

  // Prevent multiple executions of the callback
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Guard against multiple executions
    if (hasProcessed.current) {
      console.log('⚠️ OAuth callback already processed, skipping')
      return
    }

    const handleCallback = async () => {
      // Mark as processing to prevent duplicate executions
      hasProcessed.current = true
      try {
        // Handle mock OAuth callback (for local testing)
        if (authService.isMockOAuthCallback?.()) {
          console.log('🔓 Mock OAuth callback detected')
          authService.completeMockOAuthCallback?.()
        }

        // Add a small delay to ensure auth state is ready
        await new Promise(resolve => setTimeout(resolve, 300))

        // Get the current user to update auth state
        const user = await authService.getCurrentUser()
        if (!user) {
          throw new Error('Failed to get user details')
        }

        // start tracking the user
        if (!(await authService.isInternalUser())) {
          identifyUser(user.username)
          setUserProperties({
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
          })

          trackAuth('signed_in', {
            $email: user.email,
            $first_name: user.firstName,
            $last_name: user.lastName,
          })
        }

        // Check for pending file upload
        const pendingUpload = getPendingUpload()

        if (pendingUpload) {
          setProcessingMessage('Starting analysis...')

          try {
            const { file, detectedType } = pendingUpload

            // Store file in Redux
            if (detectedType === FILE_TYPE.VIDEO) {
              dispatch(setVideoFile(file))
            } else {
              dispatch(setImageFile(file))
            }

            // Upload to S3
            const s3Uri = await uploadPendingFile(file)
            dispatch(setMediaUri(s3Uri))

            // Clear pending upload
            clearPendingUpload()

            // Track analysis
            trackAnalysis(detectedType)

            // Navigate to analysis page
            navigate(AnalyzerMap[detectedType], { replace: true })
            return
          } catch (error) {
            console.error('Failed to upload pending file:', error)
            clearPendingUpload()
            toast.error('Failed to upload file. Please try again.')
            navigate('/', { replace: true })
            return
          }
        }

        // No pending upload - redirect to saved location or home
        const redirectTo =
          localStorage.getItem('auth_redirect_after_signin') || '/'
        localStorage.removeItem('auth_redirect_after_signin')
        navigate(redirectTo, { replace: true })
      } catch (error) {
        console.error('OAuth callback error:', error)
        toast.error('Failed to complete sign in')
        clearPendingUpload()
        // Reset the flag on error to allow retry
        hasProcessed.current = false
        navigate('/auth/signin', { replace: true })
      }
    }

    handleCallback()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array - only run once on mount

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center space-y-4">
        <ProcessingProgress message={processingMessage} />
      </div>
    </div>
  )
}
